"use server";

import { getAppUrl } from "@/lib/app-url";

const STRAPI_API_URL = process.env.STRAPI_API_URL;

export interface AuthResult {
  success: boolean;
  error?: string;
  redirectUrl?: string;
  email?: string;
  requiresVerification?: boolean;
}

export interface VerifyEmailResult {
  success: boolean;
  error?: string;
  redirectUrl?: string;
}

export interface SignupCredentials {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  plan?: "monthly" | "yearly";
  googleId?: string;
  googlePicture?: string;
}

/**
 * Register new user in Strapi
 * - Google signups: Strapi returns redirectUrl with auth code → redirect to CRM
 * - Email/password signups: Strapi sends verification email, returns requiresVerification
 */
export async function signupAction(
  credentials: SignupCredentials
): Promise<AuthResult> {
  try {
    if (!STRAPI_API_URL) {
      return { success: false, error: "Server configuration error" };
    }

    const registrationData: Record<string, string | undefined> = {
      username: credentials.email,
      email: credentials.email,
      password: credentials.password,
      firstName: credentials.firstName,
      lastName: credentials.lastName,
    };

    if (credentials.googleId) registrationData.googleId = credentials.googleId;
    if (credentials.googlePicture) registrationData.googlePicture = credentials.googlePicture;
    // Tell the backend which landing domain we're on so it hands off to the
    // matching CRM domain (coldfirecrm.com -> app.coldfirecrm.com, etc.).
    registrationData.origin = await getAppUrl();

    // Shared secret so the backend trusts a Google (googleId) signup as verified —
    // harmless for plain email/password signups, which it never auto-confirms.
    const response = await fetch(`${STRAPI_API_URL}/api/saas-auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-saas-auth-secret": process.env.SAAS_AUTH_SHARED_SECRET || "",
      },
      body: JSON.stringify(registrationData),
    });

    const data = await response.json();

    if (!response.ok) {
      if (data.error?.message?.includes("Email") || data.error?.message?.includes("email")) {
        return { success: false, error: "An account with this email already exists" };
      }
      if (data.error?.message?.includes("username")) {
        return { success: false, error: "This username is already taken" };
      }
      return { success: false, error: data.error?.message || "Registration failed" };
    }

    // Google signup: backend returns redirectUrl with auth code
    if (data.verified && data.redirectUrl) {
      const url = new URL(data.redirectUrl);
      if (credentials.plan) url.searchParams.set("plan", credentials.plan);
      return { success: true, redirectUrl: url.toString() };
    }

    // Email/password signup: backend sent verification email
    return {
      success: true,
      requiresVerification: true,
      email: data.user.email,
    };
  } catch (error) {
    console.error("Signup error:", error);
    return { success: false, error: "An unexpected error occurred. Please try again." };
  }
}

/**
 * Verify email with 6-digit code
 * Strapi returns redirectUrl with auth code → redirect to CRM
 */
export async function verifyEmailAction(
  email: string,
  code: string,
  plan?: string
): Promise<VerifyEmailResult> {
  try {
    if (!STRAPI_API_URL) {
      return { success: false, error: "Server configuration error" };
    }

    const response = await fetch(`${STRAPI_API_URL}/api/saas-auth/verify-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.toLowerCase(), code, origin: await getAppUrl() }),
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error?.message || "Invalid verification code" };
    }

    if (!data.redirectUrl) {
      return { success: false, error: "Verification failed" };
    }

    const url = new URL(data.redirectUrl);
    if (plan) url.searchParams.set("plan", plan);

    return { success: true, redirectUrl: url.toString() };
  } catch (error) {
    console.error("Verify email error:", error);
    return { success: false, error: "An unexpected error occurred. Please try again." };
  }
}

/**
 * Resend verification code (Strapi sends the email)
 */
export async function resendVerificationAction(
  email: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (!STRAPI_API_URL) {
      return { success: false, error: "Server configuration error" };
    }

    const response = await fetch(`${STRAPI_API_URL}/api/saas-auth/resend-verification`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.toLowerCase() }),
    });

    if (!response.ok) {
      const data = await response.json();
      return { success: false, error: data.error?.message || "Failed to resend code" };
    }

    return { success: true };
  } catch (error) {
    console.error("Resend verification error:", error);
    return { success: false, error: "An unexpected error occurred" };
  }
}
